import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { themes, defaultTheme } from '../themes';
import typographyTokens from '../tokens/typography';
import {
  colors,
  gradients,
  operationalElevation,
  operationalRadii,
  operationalStatusColors,
  operationalSurfaces
} from '../tokens';

const FONT_OPTIONS = [
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
];

const FONT_SIZE_OPTIONS = [
  { label: 'Compacto', value: '0.75rem' },
  { label: 'Confortavel', value: '0.95rem' },
];

const defaultFontFamily = 'Montserrat, sans-serif';
const defaultFontSize = '0.75rem';
let themeSwitchTransitionTimer = null;

const getOperationalSurfaceTokens = (mode) => (
  mode === 'dark' ? operationalSurfaces.dark : operationalSurfaces.light
);

const markThemeSwitching = () => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.classList.add('theme-switching');
  document.body?.classList.add('theme-switching');

  if (themeSwitchTransitionTimer) {
    clearTimeout(themeSwitchTransitionTimer);
  }

  themeSwitchTransitionTimer = setTimeout(() => {
    root.classList.remove('theme-switching');
    document.body?.classList.remove('theme-switching');
    themeSwitchTransitionTimer = null;
  }, 180);
};

const isValidThemeId = (themeId) => {
  if (!themes || !themeId || typeof themeId !== 'string') return false;
  return !!themes[themeId];
};

const isValidFontFamily = (fontFamily) => {
  if (!fontFamily || typeof fontFamily !== 'string') return false;
  const validFonts = FONT_OPTIONS.map((font) => font.value);
  return validFonts.includes(fontFamily);
};

const isValidFontSize = (fontSize) => {
  if (!fontSize || typeof fontSize !== 'string') return false;
  const validSizes = FONT_SIZE_OPTIONS.map((size) => size.value);
  return validSizes.includes(fontSize);
};

const cleanupLocalStorage = () => {
  const cleaned = {
    removedKeys: [],
    fixedValues: {},
  };

  const savedThemeId = localStorage.getItem('themeId');
  if (savedThemeId && !isValidThemeId(savedThemeId)) {
    console.warn(`[ThemeProvider] Removendo themeId invalido: ${savedThemeId}`);
    localStorage.removeItem('themeId');
    cleaned.removedKeys.push('themeId');
    cleaned.fixedValues.themeId = defaultTheme.id;
  }

  const savedFontFamily = localStorage.getItem('fontFamily');
  if (savedFontFamily && !isValidFontFamily(savedFontFamily)) {
    console.warn(`[ThemeProvider] Removendo fontFamily invalida: ${savedFontFamily}`);
    localStorage.removeItem('fontFamily');
    cleaned.removedKeys.push('fontFamily');
    cleaned.fixedValues.fontFamily = defaultFontFamily;
  }

  const savedFontSize = localStorage.getItem('fontSize');
  if (savedFontSize && !isValidFontSize(savedFontSize)) {
    console.warn(`[ThemeProvider] Removendo fontSize invalido: ${savedFontSize}`);
    localStorage.removeItem('fontSize');
    cleaned.removedKeys.push('fontSize');
    cleaned.fixedValues.fontSize = defaultFontSize;
  }

  if (localStorage.getItem('primaryColor') !== null) {
    localStorage.removeItem('primaryColor');
    cleaned.removedKeys.push('primaryColor');
  }

  if (localStorage.getItem('customAccentColor') !== null) {
    localStorage.removeItem('customAccentColor');
    cleaned.removedKeys.push('customAccentColor');
  }

  const savedShowDescriptions = localStorage.getItem('showDescriptions');
  if (savedShowDescriptions) {
    try {
      const parsed = JSON.parse(savedShowDescriptions);
      if (typeof parsed !== 'boolean') {
        console.warn(`[ThemeProvider] Removendo showDescriptions invalido: ${savedShowDescriptions}`);
        localStorage.removeItem('showDescriptions');
        cleaned.removedKeys.push('showDescriptions');
        cleaned.fixedValues.showDescriptions = true;
      }
    } catch (e) {
      console.warn(`[ThemeProvider] Erro ao parsear showDescriptions: ${e.message}`);
      localStorage.removeItem('showDescriptions');
      cleaned.removedKeys.push('showDescriptions');
      cleaned.fixedValues.showDescriptions = true;
    }
  }

  if (cleaned.removedKeys.length > 0) {
    console.info('[ThemeProvider] Limpeza automatica do localStorage concluida:', {
      removidos: cleaned.removedKeys,
      valoresCorrigidos: cleaned.fixedValues,
    });
  }

  return cleaned;
};

export const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const cleanupResult = useMemo(() => cleanupLocalStorage(), []);

  const [themeId, setThemeId] = useState(() => {
    const savedTheme = localStorage.getItem('themeId');
    return savedTheme && themes[savedTheme]
      ? savedTheme
      : (cleanupResult.fixedValues.themeId || defaultTheme.id);
  });

  const fontFamily = defaultFontFamily;

  const [fontSize, setFontSize] = useState(() =>
    localStorage.getItem('fontSize') || (cleanupResult.fixedValues.fontSize || defaultFontSize)
  );

  const customAccentColor = null;
  const setCustomAccentColor = useCallback(() => {}, []);

  useEffect(() => {
    localStorage.removeItem('customAccentColor');
    document.documentElement.style.removeProperty('--custom-accent-color');
    document.documentElement.style.removeProperty('--custom-accent-text-color');
  }, []);

  const [showDescriptions, setShowDescriptions] = useState(() => {
    const value = localStorage.getItem('showDescriptions');
    if (value !== null) {
      try {
        return JSON.parse(value);
      } catch (e) {
        return cleanupResult.fixedValues.showDescriptions !== undefined
          ? cleanupResult.fixedValues.showDescriptions
          : true;
      }
    }
    return true;
  });

  const currentTheme = useMemo(() => {
    const baseTheme = themes[themeId] || defaultTheme;

    const customTheme = {
      ...baseTheme,
      typography: {
        ...baseTheme.typography,
        fontFamily,
      },
      palette: {
        ...baseTheme.palette,
        error: {
          main: colors.error.main,
          dark: colors.error.dark,
          light: colors.error.light,
          contrastText: '#fff',
        },
        warning: {
          main: operationalStatusColors.warning,
          dark: '#f57c00',
          light: '#ffb74d',
          contrastText: '#000',
        },
      },
    };

    const ensureTypographyColors = () => {
      const baseTypography = baseTheme.components?.MuiTypography?.styleOverrides || {};

      return {
        ...baseTypography,
        root: {
          ...(baseTypography.root || {}),
          fontFamily,
          color: customTheme.palette.text.primary,
        },
        h1: { ...(baseTypography.h1 || {}), color: customTheme.palette.text.primary },
        h2: { ...(baseTypography.h2 || {}), color: customTheme.palette.text.primary },
        h3: { ...(baseTypography.h3 || {}), color: customTheme.palette.text.primary },
        h4: { ...(baseTypography.h4 || {}), color: customTheme.palette.text.primary },
        h5: { ...(baseTypography.h5 || {}), color: customTheme.palette.text.primary },
        h6: { ...(baseTypography.h6 || {}), color: customTheme.palette.text.primary },
        subtitle1: { ...(baseTypography.subtitle1 || {}), color: customTheme.palette.text.primary },
        subtitle2: { ...(baseTypography.subtitle2 || {}), color: customTheme.palette.text.primary },
        body1: { ...(baseTypography.body1 || {}), color: customTheme.palette.text.primary },
        body2: { ...(baseTypography.body2 || {}), color: customTheme.palette.text.secondary },
      };
    };

    customTheme.components = {
      ...(baseTheme.components || {}),
      MuiCssBaseline: {
        ...baseTheme.components?.MuiCssBaseline,
        styleOverrides: {
          ...(baseTheme.components?.MuiCssBaseline?.styleOverrides || {}),
          body: {
            ...(baseTheme.components?.MuiCssBaseline?.styleOverrides?.body || {}),
            backgroundColor: customTheme.palette.background.default,
            color: customTheme.palette.text.primary,
          },
        },
      },
      MuiTypography: {
        ...baseTheme.components?.MuiTypography,
        styleOverrides: ensureTypographyColors(),
      },
      MuiButton: {
        ...baseTheme.components?.MuiButton,
        styleOverrides: {
          ...(baseTheme.components?.MuiButton?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiButton?.styleOverrides?.root || {}),
            fontFamily,
            borderRadius: operationalRadii.control,
            boxShadow: 'none',
            minHeight: 36,
            textTransform: 'none',
          },
          contained: {
            ...(baseTheme.components?.MuiButton?.styleOverrides?.contained || {}),
            boxShadow: 'none',
            '&:hover': {
              ...(baseTheme.components?.MuiButton?.styleOverrides?.contained?.['&:hover'] || {}),
              boxShadow: operationalElevation.surface,
            },
          },
        },
      },
      MuiIconButton: {
        ...baseTheme.components?.MuiIconButton,
        styleOverrides: {
          ...(baseTheme.components?.MuiIconButton?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiIconButton?.styleOverrides?.root || {}),
            borderRadius: operationalRadii.control,
          },
        },
      },
      MuiCard: {
        ...baseTheme.components?.MuiCard,
        styleOverrides: {
          ...(baseTheme.components?.MuiCard?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiCard?.styleOverrides?.root || {}),
            borderRadius: operationalRadii.surface,
            boxShadow: operationalElevation.surface,
            backgroundImage: 'none',
            border: `1px solid ${customTheme.palette.divider}`,
          },
        },
      },
      MuiPaper: {
        ...baseTheme.components?.MuiPaper,
        styleOverrides: {
          ...(baseTheme.components?.MuiPaper?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiPaper?.styleOverrides?.root || {}),
            backgroundImage: 'none',
          },
          rounded: {
            ...(baseTheme.components?.MuiPaper?.styleOverrides?.rounded || {}),
            borderRadius: operationalRadii.surface,
          },
          elevation1: {
            ...(baseTheme.components?.MuiPaper?.styleOverrides?.elevation1 || {}),
            boxShadow: operationalElevation.surface,
          },
          elevation2: {
            ...(baseTheme.components?.MuiPaper?.styleOverrides?.elevation2 || {}),
            boxShadow: operationalElevation.raised,
          },
          elevation3: {
            ...(baseTheme.components?.MuiPaper?.styleOverrides?.elevation3 || {}),
            boxShadow: operationalElevation.raised,
          },
        },
      },
      MuiDialog: {
        ...baseTheme.components?.MuiDialog,
        styleOverrides: {
          ...(baseTheme.components?.MuiDialog?.styleOverrides || {}),
          paper: {
            ...(baseTheme.components?.MuiDialog?.styleOverrides?.paper || {}),
            borderRadius: operationalRadii.modal,
            boxShadow: operationalElevation.modal,
            backgroundImage: 'none',
            border: `1px solid ${customTheme.palette.divider}`,
          },
        },
      },
      MuiInputBase: {
        ...baseTheme.components?.MuiInputBase,
        styleOverrides: {
          ...(baseTheme.components?.MuiInputBase?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiInputBase?.styleOverrides?.root || {}),
            fontFamily,
          },
        },
      },
      MuiOutlinedInput: {
        ...baseTheme.components?.MuiOutlinedInput,
        styleOverrides: {
          ...(baseTheme.components?.MuiOutlinedInput?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiOutlinedInput?.styleOverrides?.root || {}),
            borderRadius: operationalRadii.control,
            backgroundColor: customTheme.palette.mode === 'dark'
              ? getOperationalSurfaceTokens('dark').panelRaised
              : getOperationalSurfaceTokens('light').panel,
            '& .MuiOutlinedInput-notchedOutline': {
              ...(baseTheme.components?.MuiOutlinedInput?.styleOverrides?.root?.['& .MuiOutlinedInput-notchedOutline'] || {}),
              borderRadius: operationalRadii.control,
            },
          },
        },
      },
      MuiChip: {
        ...baseTheme.components?.MuiChip,
        styleOverrides: {
          ...(baseTheme.components?.MuiChip?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiChip?.styleOverrides?.root || {}),
            borderRadius: operationalRadii.pill,
            fontWeight: 600,
          },
          sizeSmall: {
            ...(baseTheme.components?.MuiChip?.styleOverrides?.sizeSmall || {}),
            height: 24,
          },
        },
      },
      MuiTableCell: {
        ...baseTheme.components?.MuiTableCell,
        styleOverrides: {
          ...(baseTheme.components?.MuiTableCell?.styleOverrides || {}),
          root: {
            ...(baseTheme.components?.MuiTableCell?.styleOverrides?.root || {}),
            borderBottomColor: customTheme.palette.divider,
          },
          head: {
            ...(baseTheme.components?.MuiTableCell?.styleOverrides?.head || {}),
            color: customTheme.palette.text.secondary,
            fontSize: '0.72rem',
            fontWeight: 800,
            letterSpacing: 0,
            textTransform: 'uppercase',
            backgroundColor: customTheme.palette.mode === 'dark'
              ? getOperationalSurfaceTokens('dark').panelRaised
              : getOperationalSurfaceTokens('light').panelRaised,
          },
        },
      },
    };

    return customTheme;
  }, [themeId, fontFamily]);

  useEffect(() => {
    localStorage.setItem('themeId', themeId);
  }, [themeId]);

  useEffect(() => {
    localStorage.setItem('fontFamily', defaultFontFamily);
  }, [fontFamily]);

  useEffect(() => {
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem('showDescriptions', JSON.stringify(showDescriptions));
  }, [showDescriptions]);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'theme-font-size';
    styleElement.textContent = `
      .MuiTypography-root:not(.MuiTypography-h1):not(.MuiTypography-h2):not(.MuiTypography-h3):not(.MuiTypography-h4):not(.MuiTypography-h5):not(.MuiTypography-h6):not(.MuiTypography-subtitle1):not(.MuiTypography-subtitle2),
      .MuiButton-root,
      .MuiInputBase-input,
      .MuiMenuItem-root,
      .MuiListItemText-primary,
      .MuiListItemText-secondary,
      .MuiListItemButton-root,
      .MuiChip-label {
        font-size: ${fontSize} !important;
      }

      body {
        font-size: ${fontSize};
      }
    `;

    const oldStyle = document.getElementById('theme-font-size');
    if (oldStyle) {
      oldStyle.remove();
    }

    document.head.appendChild(styleElement);

    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, [fontSize]);

  const applyThemeCssVariables = useCallback((palette) => {
    const root = document.documentElement;
    const isDark = palette.mode === 'dark';
    const surfaces = getOperationalSurfaceTokens(palette.mode);

    root.style.colorScheme = palette.mode;
    root.classList.toggle('light-mode', !isDark);
    root.classList.toggle('dark-mode', isDark);
    document.body?.classList.toggle('light-mode', !isDark);
    document.body?.classList.toggle('dark-mode', isDark);
    root.style.setProperty('--background-default', palette.background.default);
    root.style.setProperty('--background-paper', palette.background.paper);
    root.style.setProperty('--text-primary', palette.text.primary);
    root.style.setProperty('--text-secondary', palette.text.secondary);
    root.style.setProperty('--divider', palette.divider);
    root.style.setProperty('--layout-shell-bg', surfaces.shell);
    root.style.setProperty('--layout-stage-bg', surfaces.stage);
    // Ensure the sidebar gets the same color for a seamless look
    root.style.setProperty('--layout-sidebar-bg', surfaces.shell);
    root.style.setProperty('--layout-stage-shadow', isDark
      ? '0 1px 2px rgba(0, 0, 0, 0.20)'
      : '0 1px 2px rgba(15, 23, 42, 0.08)');
    root.style.setProperty('--primary-gradient', palette.primary.main);
    root.style.setProperty('--has-gradient', '0');
  }, []);

  useEffect(() => {
    const baseTheme = themes[themeId] || defaultTheme;
    applyThemeCssVariables(baseTheme.palette);
  }, [themeId, applyThemeCssVariables]);

  const toggleDarkMode = useCallback(() => {
    markThemeSwitching();
    setThemeId((prev) => (prev === 'light' ? 'escuro' : 'light'));
  }, []);

  const setTheme = useCallback((newThemeId) => {
    if (themes[newThemeId]) {
      markThemeSwitching();
      setThemeId((prev) => (prev === newThemeId ? prev : newThemeId));
    }
  }, []);

  const setThemeIdWithGuard = useCallback((nextThemeId) => {
    markThemeSwitching();
    setThemeId(nextThemeId);
  }, []);

  const resetThemeSettings = useCallback(() => {
    localStorage.removeItem('themeId');
    localStorage.removeItem('fontSize');
    localStorage.removeItem('primaryColor');
    localStorage.removeItem('showDescriptions');

    markThemeSwitching();
    setThemeId(defaultTheme.id);
    setFontSize(defaultFontSize);
    setShowDescriptions(true);

    console.info('[ThemeProvider] Configuracoes de tema resetadas para valores padrao');
  }, []);

  const dynamicFontOptions = useMemo(() => {
    try {
      if (typographyTokens && typographyTokens.fontFamilies) {
        return Object.values(typographyTokens.fontFamilies);
      }
    } catch (e) {
      console.warn('Erro ao carregar tokens de tipografia (fontes):', e);
    }
    return FONT_OPTIONS;
  }, []);

  const dynamicFontSizeOptions = useMemo(() => {
    try {
      if (typographyTokens && typographyTokens.fontSizes) {
        return Object.values(typographyTokens.fontSizes);
      }
    } catch (e) {
      console.warn('Erro ao carregar tokens de tipografia (tamanhos):', e);
    }
    return FONT_SIZE_OPTIONS;
  }, []);

  const muiTheme = useMemo(() => createTheme(currentTheme), [currentTheme]);

  const setFontFamily = useCallback(() => {}, []);
  const setPrimaryColor = useCallback(() => {}, []);
  const getPrimaryBackground = useCallback(() => currentTheme.palette.primary.main, [currentTheme.palette.primary.main]);

  const value = useMemo(() => ({
    currentTheme,
    isDarkMode: currentTheme.palette.mode === 'dark',
    themes,
    toggleDarkMode,
    setTheme,
    themeId,
    setThemeId: setThemeIdWithGuard,
    fontFamily: defaultFontFamily,
    setFontFamily,
    fontSize,
    setFontSize,
    primaryColor: null,
    setPrimaryColor,
    PRIMARY_COLOR_OPTIONS: [],
    GRADIENT_COLOR_OPTIONS: [],
    primaryGradient: null,
    isPrimaryGradient: false,
    FONT_OPTIONS,
    FONT_SIZE_OPTIONS,
    fontOptions: dynamicFontOptions,
    fontSizeOptions: dynamicFontSizeOptions,
    showDescriptions,
    setShowDescriptions,
    customAccentColor,
    setCustomAccentColor,
    gradients,
    miaColors: colors.mia,
    socialMediaColors: colors.socialMedia,
    sentimentColors: colors.sentiment,
    exposureColors: colors.exposure,
    groupColors: colors.groupColors,
    glassColors: colors.glass,
    getPrimaryBackground,
    resetThemeSettings,
  }), [
    currentTheme,
    toggleDarkMode,
    setTheme,
    themeId,
    setThemeIdWithGuard,
    setFontFamily,
    fontSize,
    setPrimaryColor,
    dynamicFontOptions,
    dynamicFontSizeOptions,
    showDescriptions,
    customAccentColor,
    setCustomAccentColor,
    getPrimaryBackground,
    resetThemeSettings
  ]);

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export const useViewSettings = useTheme;

export default ThemeProvider;
